import { signInWithEmailAndPassword, signOut as firebaseSignOut, AuthError } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';

interface AuthResponse {
  user: any | null;
  error: Error | null;
}

const getErrorMessage = (error: AuthError): string => {
  switch (error.code) {
    case 'auth/invalid-credential':
      return 'Email ou mot de passe incorrect';
    case 'auth/invalid-email':
      return 'Adresse email invalide';
    case 'auth/user-disabled':
      return 'Ce compte a été désactivé';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
      return 'Email ou mot de passe incorrect';
    case 'auth/too-many-requests':
      return 'Trop de tentatives de connexion. Veuillez réessayer plus tard';
    case 'auth/network-request-failed':
      return 'Erreur de connexion réseau. Veuillez vérifier votre connexion internet';
    default:
      console.error('Unhandled auth error:', error.code, error.message);
      return 'Une erreur est survenue lors de la connexion';
  }
};

export const signIn = async (email: string, password: string): Promise<AuthResponse> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    // Check if user is admin
    const adminDoc = await getDoc(doc(db, 'admins', userCredential.user.uid));
    
    if (!adminDoc.exists()) {
      await firebaseSignOut(auth);
      return {
        user: null,
        error: new Error('Accès non autorisé. Seuls les administrateurs peuvent se connecter.')
      };
    }

    return { user: userCredential.user, error: null };
  } catch (error: any) {
    console.error('Sign in error:', {
      code: error.code,
      message: error.message,
      fullError: error
    });
    return { 
      user: null, 
      error: new Error(getErrorMessage(error as AuthError))
    };
  }
};

export const signOut = async (): Promise<{ error: Error | null }> => {
  try {
    await firebaseSignOut(auth);
    return { error: null };
  } catch (error) {
    console.error('Sign out error:', error);
    return { 
      error: new Error('Une erreur est survenue lors de la déconnexion')
    };
  }
};

export const isAdmin = async (uid: string): Promise<boolean> => {
  try {
    const adminDoc = await getDoc(doc(db, 'admins', uid));
    return adminDoc.exists();
  } catch (error) {
    console.error('Admin check error:', error);
    return false;
  }
};